import {
    PLOT_ABLAUF_WARTET_TAGE,
    PLOT_ERREICHBAR_SEK,
    PLOT_MAX_KOPIEN,
    PLOT_TIMEOUT_DRUCKT_MIN,
    PLOT_VERZOEGERT_MIN,
} from './plotTypen';
import type { PlotAgent, PlotAnlageStatus, PlotAuftrag, PlotDokument, PlotErreichbarkeit, PlotStatus, PlotTon } from './plotTypen';

/**
 * Reine Regeln des Plotter-Drucks — ohne Supabase, ohne Uhr: wer die Zeit
 * braucht, bekommt sie als `jetztMs` herein (Tests, React-Compiler `purity`).
 *
 * Zeitstempel kommen vom Server (PostgREST/jsonb: ISO mit Mikrosekunden und
 * `+00:00`). Was sich nicht lesen lässt, ändert nichts an der Anzeige: dann
 * gilt, was der Server sagt.
 */

const SEK_MS = 1000;
const MIN_MS = 60 * SEK_MS;
const STD_MS = 60 * MIN_MS;
const TAG_MS = 24 * STD_MS;

/**
 * Postgres-Schreibweise `2026-10-15 08:00:00.123456+00` (Leerzeichen, kurze
 * Zone, sechs Nachkommastellen) liest nicht jeder Browser — Safari scheitert
 * daran. Sie wird vor dem Parsen in ISO gebracht; ISO bleibt unverändert.
 */
const PG_ZEIT = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)(?:\.(\d+))?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i;

/** Millisekunden seit 1970 oder `null`, wenn der Wert fehlt oder unlesbar ist. */
const zeitMs = (wert: string | null | undefined): number | null => {
    if (typeof wert !== 'string') return null;
    const roh = wert.trim();
    if (!roh) return null;
    const m = PG_ZEIT.exec(roh);
    let iso = roh;
    if (m) {
        const [, datum, uhr, bruch, zone] = m;
        const ms = bruch ? `.${bruch.slice(0, 3).padEnd(3, '0')}` : '';
        let z = zone ? zone.toUpperCase() : '';
        if (/^[+-]\d{2}$/.test(z)) z = `${z}:00`;
        else if (/^[+-]\d{4}$/.test(z)) z = `${z.slice(0, 3)}:${z.slice(3)}`;
        iso = `${datum}T${uhr}${ms}${z}`;
    }
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : null;
};

/** „3 Min.“, „2 Std.“, „1 Tag“, „4 Tagen“ — passt hinter „vor“ und „seit“. */
const dauerText = (ms: number): string => {
    if (ms < STD_MS) return `${Math.max(1, Math.floor(ms / MIN_MS))} Min.`;
    if (ms < TAG_MS) return `${Math.floor(ms / STD_MS)} Std.`;
    const tage = Math.floor(ms / TAG_MS);
    return tage === 1 ? '1 Tag' : `${tage} Tagen`;
};

// ─── Status ─────────────────────────────────────────────────────────────────

/**
 * Status für die Anzeige: `druckt` ohne Lebenszeichen seit
 * PLOT_TIMEOUT_DRUCKT_MIN gilt als `unklar`, `wartet` älter als
 * PLOT_ABLAUF_WARTET_TAGE als `abgelaufen` — auch wenn der Server das erst beim
 * nächsten Abholen festschreibt (läuft kein Agent, schreibt ihn niemand fest).
 *
 * Grenzen wie im SQL: „älter als“ heißt echt größer — genau 15 Minuten bzw.
 * genau 7 Tage sind noch `druckt` bzw. `wartet`. Bezug für `druckt` ist
 * `coalesce(lebenszeichen_am, abgeholt_am)`, für `wartet` `erstellt_am`.
 *
 * `jetztMs` ist die Uhr des Aufrufers. Wer die Geräteuhr auf die Serveruhr
 * ziehen will, übergibt `jetztMs + uhrVersatzMs(status)`.
 */
export function anzeigeStatus(a: PlotAuftrag, jetztMs: number): PlotStatus {
    if (!Number.isFinite(jetztMs)) return a.status;
    if (a.status === 'druckt') {
        // Wie coalesce: ein gesetztes, aber unlesbares Lebenszeichen fällt
        // NICHT auf abgeholt_am zurück — unlesbar heißt Server-Status.
        const bezug = zeitMs(a.lebenszeichen_am ?? a.abgeholt_am);
        if (bezug !== null && jetztMs - bezug > PLOT_TIMEOUT_DRUCKT_MIN * MIN_MS) return 'unklar';
        return 'druckt';
    }
    if (a.status === 'wartet') {
        const erstellt = zeitMs(a.erstellt_am);
        if (erstellt !== null && jetztMs - erstellt > PLOT_ABLAUF_WARTET_TAGE * TAG_MS) return 'abgelaufen';
        return 'wartet';
    }
    return a.status;
}

const STATUS_TEXT: Record<PlotStatus, string> = {
    wartet: 'Wartet auf den Druck-PC',
    druckt: 'Wird gedruckt',
    gedruckt: 'Gedruckt',
    fehler: 'Fehler',
    unklar: 'Unklar — bitte am Plotter prüfen',
    abgebrochen: 'Abgebrochen',
    abgelaufen: 'Abgelaufen — nicht abgeholt',
};

/** `gedruckt` heißt: an den Plotter übergeben (Windows-Warteschlange leer). */
export function statusText(s: PlotStatus): string {
    return STATUS_TEXT[s] ?? String(s);
}

const STATUS_TON: Record<PlotStatus, PlotTon> = {
    wartet: 'neutral',
    druckt: 'laeuft',
    gedruckt: 'ok',
    fehler: 'fehler',
    unklar: 'warnung',
    abgebrochen: 'neutral',
    abgelaufen: 'neutral',
};

export function statusTon(s: PlotStatus): PlotTon {
    return STATUS_TON[s] ?? 'neutral';
}

/** `wartet` oder `druckt`. */
export function istOffen(s: PlotStatus): boolean {
    return s === 'wartet' || s === 'druckt';
}

// ─── Kopien ─────────────────────────────────────────────────────────────────

/**
 * Ganzzahl 1 … PLOT_MAX_KOPIEN; alles Unlesbare wird 1.
 * Abgerundet (2,9 → 2: lieber eine Kopie zu wenig als eine zu viel auf dem
 * Plotter), Text mit Komma oder Punkt wird gelesen, Leeres/NaN/≤ 0 → 1,
 * über dem Maximum → Maximum.
 */
export function kopienBegrenzen(n: unknown): number {
    let zahl: number;
    if (typeof n === 'number') zahl = n;
    else if (typeof n === 'string') {
        const t = n.trim().replace(',', '.');
        zahl = t ? Number(t) : Number.NaN;
    } else return 1;
    if (Number.isNaN(zahl)) return 1;
    const ganz = Math.floor(zahl);
    if (ganz < 1) return 1;
    if (ganz > PLOT_MAX_KOPIEN) return PLOT_MAX_KOPIEN;
    return ganz;
}

/** Vorschlag für „Nochmal drucken“: der Rest, wenn schon Kopien übergeben sind, sonst alle. */
export function vorschlagKopienNachdruck(a: PlotAuftrag): number {
    const kopien = kopienBegrenzen(a.kopien);
    const fertig = Number.isFinite(a.kopien_fertig) ? Math.floor(a.kopien_fertig) : 0;
    if (fertig > 0 && fertig < kopien) return kopienBegrenzen(kopien - fertig);
    return kopien;
}

// ─── Erreichbarkeit des Druck-PCs ──────────────────────────────────────────

/**
 * Geräteuhr beim Empfang je Statusantwort. `plotAnlageStatusLaden` trägt sie
 * ein; der Schlüssel ist das Antwortobjekt selbst (eine Kopie per Spread
 * verliert den Eintrag und fällt auf die Rückfallregel in `serverJetztMs`).
 */
const empfangenAm = new WeakMap<PlotAnlageStatus, number>();

/** Merkt, wann (Geräteuhr) diese Antwort ankam. Aufrufer: plotAuftraege.ts. */
export function statusEmpfangenMerken(status: PlotAnlageStatus, geraetMs: number): void {
    if (Number.isFinite(geraetMs)) empfangenAm.set(status, geraetMs);
}

/**
 * Serveruhr minus Geräteuhr in ms, gemessen beim Empfang der Antwort; 0, wenn
 * das unbekannt ist. `Date.now() + uhrVersatzMs(status)` ist die Serveruhr —
 * damit lassen sich auch `anzeigeStatus` und `relativeZeit` gegen eine falsch
 * gehende Geräteuhr absichern. Die Laufzeit der Antwort (Millisekunden)
 * steckt mit drin und ist gegen die Grenzen von Minuten bedeutungslos.
 */
export function uhrVersatzMs(status: PlotAnlageStatus | null): number {
    if (!status) return 0;
    const server = zeitMs(status.jetzt);
    const empfang = empfangenAm.get(status);
    if (server === null || empfang === undefined) return 0;
    return server - empfang;
}

/**
 * Die Serveruhr „jetzt“, so gut sie sich schätzen lässt. `letzter_kontakt`
 * stammt von der Serveruhr; gegen die Geräteuhr gerechnet würde eine
 * vorgehende Uhr einen gesunden Druck-PC als „verzögert“ melden, eine
 * nachgehende einen toten als „erreichbar“.
 *
 *  1. Empfangszeit bekannt: Geräteuhr + Versatz. Läuft mit, ohne Versatz.
 *  2. Nur `status.jetzt` lesbar: das Größere aus Geräteuhr und Serveruhr bei
 *     der Antwort. Die Serveruhr ist seither nur weitergelaufen, also ist sie
 *     eine untere Schranke — eine nachgehende Geräteuhr kann einen toten
 *     Druck-PC damit nicht mehr schönrechnen.
 *  3. Sonst: die Geräteuhr.
 */
const serverJetztMs = (status: PlotAnlageStatus, geraetJetztMs: number): number => {
    const server = zeitMs(status.jetzt);
    if (server === null) return geraetJetztMs;
    const empfang = empfangenAm.get(status);
    if (empfang !== undefined) return geraetJetztMs + (server - empfang);
    return Math.max(geraetJetztMs, server);
};

/** Anführungszeichen um den Namen, wie im Satz gebraucht. */
const inAnfuehrung = (name: string): string => `„${name}“`;

/**
 * `status` null = SQL fehlt (`nicht_eingerichtet`). Gesperrte Agenten zählen nicht.
 * Von mehreren Druck-PCs zählt der mit dem jüngsten Kontakt — einer, der
 * abholt, reicht.
 */
export function erreichbarkeit(status: PlotAnlageStatus | null, jetztMs: number): PlotErreichbarkeit {
    if (!status) {
        return { stufe: 'nicht_eingerichtet', text: 'Plotter-Druck ist in dieser Umgebung noch nicht eingerichtet.' };
    }
    const aktive = (status.agenten ?? []).filter((a) => !a.gesperrt);
    if (aktive.length === 0) {
        // Der Druck-PC wird seit dem Menü-Umbau (19.10.2026) im Reiter der
        // SiGe-Plan-Vorlagen angemeldet, nicht mehr in den Einstellungen.
        return { stufe: 'kein_agent', text: 'Kein Druck-PC angemeldet — ein Admin richtet ihn unter Kataloge & Vorlagen → SiGe-Plan-Vorlagen ein.' };
    }

    let bester: PlotAgent = aktive[0];
    let besterKontakt = zeitMs(bester.letzter_kontakt);
    for (const a of aktive.slice(1)) {
        const k = zeitMs(a.letzter_kontakt);
        if (k !== null && (besterKontakt === null || k > besterKontakt)) {
            bester = a;
            besterKontakt = k;
        }
    }

    const name = (bester.name ?? '').trim() || 'Druck-PC';
    const basis = { agentName: name, letzterKontakt: bester.letzter_kontakt ?? null };
    const wer = `Druck-PC ${inAnfuehrung(name)}`;

    if (besterKontakt === null) {
        return {
            ...basis,
            stufe: 'nie_verbunden',
            text: `${wer} ist angemeldet, hat sich aber noch nie gemeldet — Aufträge warten, bis er läuft.`,
        };
    }

    // Ein Kontakt „in der Zukunft“ (Rest-Uhrversatz) ist gerade eben.
    const alter = Math.max(0, serverJetztMs(status, jetztMs) - besterKontakt);
    if (alter <= PLOT_ERREICHBAR_SEK * SEK_MS) {
        return { ...basis, stufe: 'erreichbar', text: `${wer} ist erreichbar.` };
    }
    if (alter <= PLOT_VERZOEGERT_MIN * MIN_MS) {
        return {
            ...basis,
            stufe: 'verzoegert',
            text: `${wer} hat sich seit ${dauerText(alter)} nicht gemeldet — der Druck kann sich verzögern.`,
        };
    }
    return {
        ...basis,
        stufe: 'nicht_erreichbar',
        text: `${wer} ist seit ${dauerText(alter)} nicht erreichbar — Aufträge warten, bis er wieder läuft.`,
    };
}

// ─── Dateiname, Titel, Pfad ────────────────────────────────────────────────

/** Was Windows im Dateinamen verbietet, dazu alle Steuerzeichen. */
const VERBOTEN = /[<>:"/\\|?*\u0000-\u001f\u007f]/g;
const DATEINAME_MAX = 120;
const TITEL_MAX = 200;

/** Kürzt auf höchstens `max` UTF-16-Einheiten, ohne ein Ersatzzeichenpaar (Emoji) zu zerreißen. */
const kuerzen = (text: string, max: number): string => {
    if (text.length <= max) return text;
    let aus = '';
    for (const zeichen of text) {
        if (aus.length + zeichen.length > max) break;
        aus += zeichen;
    }
    return aus;
};

const dateinamenTeil = (wert: string | null | undefined): string =>
    (wert ?? '').trim().replace(/\s+/g, '_').replace(VERBOTEN, '-');

/** Bereinigte Teile mit `_` verbunden, höchstens DATEINAME_MAX Zeichen samt `.pdf`. */
const dateinameAusTeilen = (teile: readonly string[]): string => {
    let stamm = teile.join('_');
    if (stamm.length > DATEINAME_MAX - 4) {
        // Ein Punkt oder Unterstrich am Schnitt sähe nach Absicht aus.
        stamm = kuerzen(stamm, DATEINAME_MAX - 4).replace(/[._]+$/, '');
    }
    return `${stamm}.pdf`;
};

/**
 * `SiGe-Plan_<Projekt>_<Plan>.pdf`, nur Zeichen, die Windows im Dateinamen erlaubt.
 * Fehlende Teile fallen weg (`SiGe-Plan_Entwurf.pdf`, wenn beide fehlen);
 * höchstens 120 Zeichen samt `.pdf`.
 */
export function plotDateiname(planNr: string | null | undefined, projektNummer: string | null | undefined): string {
    const teile = ['SiGe-Plan', dateinamenTeil(projektNummer), dateinamenTeil(planNr)].filter(Boolean);
    if (teile.length === 1) teile.push('Entwurf');
    return dateinameAusTeilen(teile);
}

/**
 * `Vorankuendigung_<Projekt>.pdf` (`Vorankuendigung.pdf` ohne Nummer), sonst
 * wie `plotDateiname`. „ue“ statt „ü“: der Druck-Agent schreibt Umlaute für
 * seine Ablage ohnehin um (Get-PaSauberName) — so steht in der Datenbank
 * derselbe Name wie auf dem Druck-PC.
 */
export function plotDateinameVorank(projektNummer: string | null | undefined): string {
    return dateinameAusTeilen(['Vorankuendigung', dateinamenTeil(projektNummer)].filter(Boolean));
}

const titelTeil = (wert: string | null | undefined): string =>
    (wert ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();

/** Teile mit „ · “ verbunden, höchstens TITEL_MAX Zeichen (gekürzt mit „…“). */
const titelAusTeilen = (teile: readonly string[]): string => {
    const titel = teile.join(' · ');
    return titel.length <= TITEL_MAX ? titel : `${kuerzen(titel, TITEL_MAX - 1).trimEnd()}…`;
};

/**
 * Titel des Auftrags für Liste und Agent-Protokoll, höchstens 200 Zeichen.
 * „SiGe-Plan 6051 · Neubau Kita · Plan-Nr. SGP-6051“ — leere Teile fallen weg.
 */
export function plotTitel(planNr: string | null | undefined, projektNummer: string | null | undefined, projektName: string | null | undefined): string {
    const nummer = titelTeil(projektNummer);
    const name = titelTeil(projektName);
    const plan = titelTeil(planNr);
    const teile = [nummer ? `SiGe-Plan ${nummer}` : 'SiGe-Plan'];
    if (name) teile.push(name);
    if (plan) teile.push(`Plan-Nr. ${plan}`);
    return titelAusTeilen(teile);
}

/**
 * Titel einer geplotteten Vorankündigung, Regeln wie `plotTitel`:
 * „Vorankündigung 6240 · Neubau Kita“ — leere Teile fallen weg.
 */
export function plotTitelVorank(projektNummer: string | null | undefined, projektName: string | null | undefined): string {
    const nummer = titelTeil(projektNummer);
    const name = titelTeil(projektName);
    const teile = [nummer ? `Vorankündigung ${nummer}` : 'Vorankündigung'];
    if (name) teile.push(name);
    return titelAusTeilen(teile);
}

/**
 * Der SiGe-Plan als Druckstück des Plotter-Dialogs. Schlüssel ist die ID des
 * Plans — so bleiben die Sendeabsichten bei `senden:<sigeProjektId>` wie vor
 * der Vorankündigung, und Titel und Dateiname sind dieselben wie bisher.
 * Strukturelle Typen: der Dienst kennt die Komponenten-Typen nicht.
 */
export function plotDokumentSigePlan(
    plan: { id: string; project_id?: string | null; plan_nr?: string | null },
    fusszeile?: { projektNummer?: string | null; projektName?: string | null } | null,
): PlotDokument {
    return {
        schluessel: plan.id,
        projectId: plan.project_id ?? null,
        sigeProjektId: plan.id,
        titel: plotTitel(plan.plan_nr, fusszeile?.projektNummer, fusszeile?.projektName),
        dateiname: plotDateiname(plan.plan_nr, fusszeile?.projektNummer),
        liste: { sigeProjektId: plan.id },
    };
}

/**
 * `<company_id>/<auftrag_id>.pdf` — die Form, die die Storage-Policy
 * (erster Ordner = eigene Firma) und `plot_auftrag_anlegen` (PLOT_PFAD)
 * verlangen. Die UUID klein, wie Postgres sie ausgibt.
 */
export function plotPfad(companyId: string, auftragId: string): string {
    return `${(companyId ?? '').trim()}/${(auftragId ?? '').trim().toLowerCase()}.pdf`;
}

// ─── Relative Zeit ─────────────────────────────────────────────────────────

/**
 * „gerade eben“ (unter einer Minute), „vor 3 Min.“, „vor 2 Std.“, „vor 4 Tagen“.
 * Ein Zeitpunkt in der Zukunft (Uhrversatz) ist „gerade eben“; ein
 * unlesbarer ergibt einen leeren Text.
 */
export function relativeZeit(iso: string, jetztMs: number): string {
    const t = zeitMs(iso);
    if (t === null || !Number.isFinite(jetztMs)) return '';
    const diff = jetztMs - t;
    if (diff < MIN_MS) return 'gerade eben';
    return `vor ${dauerText(diff)}`;
}
